import { ArrowDownRight } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { resolveImageUrl } from "../lib/api";
import RichText from "./RichText";

const has = (v) => typeof v === "string" && v.trim().length > 0;

const MaintenanceNote = ({ text, dark }) =>
  has(text) ? (
    <p
      data-testid="maintenance-message"
      className="font-display italic text-2xl sm:text-3xl tr-rise"
      style={{ color: dark ? "#F8F7F4" : "#1A1918", animationDelay: "520ms" }}
    >
      {text}
    </p>
  ) : null;

export default function Hero({ maintenance = false }) {
  const { settings } = useSettings();
  const layout = settings.hero_layout || "logo";
  const bgMode = settings.hero_background_mode || "plain";
  const useBgImage = bgMode === "image" && has(settings.hero_image);
  const logoSrc = has(settings.hero_logo_image) ? resolveImageUrl(settings.hero_logo_image) : null;

  // Classic text-driven hero (kept for users who switch back)
  if (layout === "classic") {
    return (
      <section
        id="home"
        data-testid="hero-section"
        className="relative min-h-screen w-full overflow-hidden"
      >
        {useBgImage && (
          <div className="absolute inset-0 -z-10" data-testid="hero-image">
            <img
              src={resolveImageUrl(settings.hero_image)}
              alt={settings.hero_title || "Atelier Trame"}
              className="w-full h-full object-cover"
              style={{ filter: "saturate(0.92) contrast(1.02)" }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(248,247,244,0.55) 0%, rgba(248,247,244,0.25) 35%, rgba(26,25,24,0.35) 100%)",
              }}
            />
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pt-40 sm:pt-48 pb-16 min-h-screen flex flex-col justify-between">
          <div>
            {has(settings.hero_eyebrow) && (
              <p className="label-eyebrow tr-rise" data-testid="hero-eyebrow" style={{ color: "#1A1918" }}>
                {settings.hero_eyebrow}
              </p>
            )}
            {has(settings.hero_title) && (
              <h1
                data-testid="hero-title"
                className="font-display tr-rise text-[16vw] sm:text-[12vw] lg:text-[10rem] leading-[0.85] tracking-tighter font-light mt-6"
                style={{ animationDelay: "120ms", color: "#1A1918" }}
              >
                {settings.hero_title}
              </h1>
            )}
            {has(settings.hero_subtitle) && (
              <p
                className="font-display italic text-3xl sm:text-4xl lg:text-5xl mt-8 max-w-xl tr-rise"
                style={{ animationDelay: "260ms", color: "#1A1918" }}
                data-testid="hero-subtitle"
              >
                {settings.hero_subtitle}
              </p>
            )}
          </div>

          <div className="tr-rise" style={{ animationDelay: "440ms" }}>
            {has(settings.hero_description) && (
              <p
                className="font-body max-w-md mb-8"
                style={{ color: useBgImage ? "#F8F7F4" : "#1A1918", textShadow: useBgImage ? "0 1px 16px rgba(0,0,0,0.35)" : "none" }}
              >
                <RichText value={settings.hero_description} as="span" />
              </p>
            )}
            {maintenance && <MaintenanceNote text={settings.maintenance_message} dark={useBgImage} />}
            {!maintenance && has(settings.hero_cta) && (
              <button
                data-testid="hero-cta-collection"
                onClick={() =>
                  document.getElementById("collezione")?.scrollIntoView({ behavior: "smooth" })
                }
                className="tr-btn"
              >
                {settings.hero_cta}
                <ArrowDownRight className="arrow w-4 h-4" strokeWidth={1.25} />
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Default "logo" layout: full-screen wordmark, then a quiet description block on scroll
  return (
    <section id="home" data-testid="hero-section" className="relative w-full">
      {/* Full-screen logo panel */}
      <div
        className="relative w-full h-screen flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: "#F8F7F4" }}
        data-testid="hero-logo-panel"
      >
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={settings.hero_title || "trame"}
            data-testid="hero-logo-image"
            className="w-full h-full object-cover tr-rise"
            style={{ animationDelay: "120ms" }}
          />
        ) : has(settings.hero_title) ? (
          <h1
            data-testid="hero-title"
            className="font-display italic text-[18vw] sm:text-[14vw] lg:text-[11rem] leading-none tr-rise"
            style={{ color: "#1A1918", animationDelay: "120ms" }}
          >
            {settings.hero_title}
          </h1>
        ) : null}
        {maintenance && (
          <div className="absolute bottom-10 inset-x-0 flex justify-center px-6 text-center">
            <MaintenanceNote text={settings.maintenance_message} />
          </div>
        )}
      </div>

      {/* Quiet description + CTA panel */}
      {!maintenance && (has(settings.hero_subtitle) || has(settings.hero_description) || has(settings.hero_cta)) && (
        <div
          className="w-full px-6 sm:px-12 py-24 sm:py-32"
          style={{ backgroundColor: "#F8F7F4" }}
          data-testid="hero-intro-panel"
        >
          <div className="max-w-3xl mx-auto text-center">
            {has(settings.hero_subtitle) && (
              <p
                className="font-display italic text-3xl sm:text-4xl lg:text-5xl"
                style={{ color: "#1A1918" }}
                data-testid="hero-subtitle"
              >
                {settings.hero_subtitle}
              </p>
            )}
            {has(settings.hero_description) && (
              <RichText
                value={settings.hero_description}
                className="font-body text-base sm:text-lg mt-8 max-w-xl mx-auto"
                style={{ color: "#5C5A56" }}
              />
            )}
            {has(settings.hero_cta) && (
              <button
                data-testid="hero-cta-collection"
                onClick={() =>
                  document.getElementById("collezione")?.scrollIntoView({ behavior: "smooth" })
                }
                className="tr-btn mt-12"
              >
                {settings.hero_cta}
                <ArrowDownRight className="arrow w-4 h-4" strokeWidth={1.25} />
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
